import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, Search, UtensilsCrossed } from 'lucide-react';
import { useLogo } from '@/shared/hooks/useLogo';
import { useTenantHomePath } from '@/shared/hooks/useTenantHomePath';
import {
  AppBackButton,
  AppHeader,
  AppHeaderLogo,
  AppMain,
  AppPageLoading,
  AppShell,
  LAYOUT_ALERT_ERROR,
  PageHero,
} from '@/shared/components/layout';
import { usePdvAdmin } from '../hooks/usePdvAdmin';
import PdvItemModal from '../components/PdvItemModal';
import type { PdvRegras } from '../../domain/ports/IPdvEngajamentoRepository';

const inputClass = 'mt-1 w-full rounded-xl border border-white/10 bg-black/30 px-3 py-2 text-sm text-white';
const cardClass  = 'rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4';

const PAPEL_BADGE: Record<string, string> = {
  destaque: 'border-orange-500/30 bg-orange-500/15 text-orange-300',
  hype:     'border-rose-500/30 bg-rose-500/15 text-rose-300',
};

function fmtData(dt: string | null | undefined): string {
  return dt ? new Date(dt).toLocaleString('pt-BR') : '—';
}

export default function PdvEngajamentoPage() {
  const navigate = useNavigate();
  const logoUrl  = useLogo();
  const { path: homePath, empresaIdAtivo } = useTenantHomePath();
  const empresaId = empresaIdAtivo ?? '';
  const pdv = usePdvAdmin(empresaId);

  const [busca, setBusca]           = useState('');
  const [soMarcados, setSoMarcados] = useState(false);

  // Regras: os campos de lista são editados como texto e convertidos ao salvar.
  const [regras, setRegras]           = useState<Pick<PdvRegras, 'hype_rotulo' | 'janela_destaque_seg'>>({ hype_rotulo: 'chopps', janela_destaque_seg: 180 });
  const [mesasTexto, setMesasTexto]   = useState('');
  const [marcosTexto, setMarcosTexto] = useState('');

  useEffect(() => {
    if (!pdv.config) return;
    setRegras({ hype_rotulo: pdv.config.hype_rotulo, janela_destaque_seg: pdv.config.janela_destaque_seg });
    setMesasTexto(pdv.config.mesas.join(', '));
    setMarcosTexto(pdv.config.marcos_hype.join(', '));
  }, [pdv.config]);

  const itens = useMemo(() => {
    const q = busca.trim().toLowerCase();
    return pdv.itens.filter(item =>
      (!soMarcados || item.papel !== 'nenhum')
      && (!q || `${item.nome} ${item.categoria ?? ''}`.toLowerCase().includes(q)),
    );
  }, [pdv.itens, busca, soMarcados]);

  function salvarRegras() {
    void pdv.salvarRegras({
      ...regras,
      mesas:       mesasTexto.split(',').map(v => v.trim()).filter(Boolean),
      marcos_hype: marcosTexto.split(',').map(v => Number(v.trim())).filter(v => Number.isFinite(v) && v > 0),
    });
  }

  if (pdv.loading) return <AppPageLoading />;

  return (
    <AppShell direction="col">
      <AppHeader
        left={<AppBackButton onClick={() => navigate(homePath)} />}
        center={<AppHeaderLogo src={logoUrl} />}
      />
      <AppMain width="wide">
        <PageHero
          title="Engajamento do PDV"
          description="Marque pratos de destaque e itens que entram no contador de hype da TV."
        />

        {pdv.erroMsg && <p className={`mb-4 ${LAYOUT_ALERT_ERROR}`}>{pdv.erroMsg}</p>}

        {!pdv.config ? (
          <p className={`${cardClass} text-sm text-white/60`}>
            A integração Saipos ainda não foi configurada pelo administrador da plataforma.
          </p>
        ) : (
          <div className="space-y-5">
            {/* Status do sync */}
            <section className={`${cardClass} grid gap-3 sm:grid-cols-4`}>
              <div>
                <p className="text-xs text-white/40">Integração</p>
                <p className={`font-semibold ${pdv.status?.ativo ? 'text-emerald-300' : 'text-rose-300'}`}>
                  {pdv.status?.ativo ? 'Ativa' : 'Pausada'}
                </p>
              </div>
              <div>
                <p className="text-xs text-white/40">Último sync</p>
                <p className="text-sm text-white">{fmtData(pdv.status?.ultimo_sync_em)}</p>
              </div>
              <div>
                <p className="text-xs text-white/40">Mesas abertas</p>
                <p className="text-white">{pdv.status?.mesas_abertas ?? 0}</p>
              </div>
              <div>
                <p className="text-xs text-white/40">Itens hoje</p>
                <p className="text-white">{pdv.status?.eventos_hoje ?? 0}</p>
              </div>
              {pdv.status?.ultimo_erro && (
                <p className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-xs text-rose-200 sm:col-span-4">
                  Último erro ({fmtData(pdv.status.ultimo_erro_em)}): {pdv.status.ultimo_erro}
                </p>
              )}
              <button
                type="button"
                onClick={() => { void pdv.sincronizar(); }}
                disabled={pdv.sincronizando}
                className="flex items-center justify-center gap-2 rounded-xl border border-white/10 py-2 text-sm text-white/80 hover:bg-white/5 disabled:opacity-40 sm:col-span-4"
              >
                <RefreshCw className={`h-4 w-4 ${pdv.sincronizando ? 'animate-spin' : ''}`} />
                Sincronizar cardápio agora
              </button>
            </section>

            {/* Cardápio */}
            <section className="space-y-3">
              <div className="flex flex-wrap gap-3">
                <div className="relative min-w-[200px] flex-1">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/30" />
                  <input
                    value={busca}
                    onChange={e => setBusca(e.target.value)}
                    placeholder="Buscar item ou categoria"
                    className="w-full rounded-xl border border-white/10 bg-black/30 py-2.5 pl-9 pr-3 text-sm text-white placeholder:text-white/30"
                  />
                </div>
                <label className="flex items-center gap-2 text-sm text-white/70">
                  <input type="checkbox" checked={soMarcados} onChange={e => setSoMarcados(e.target.checked)} />
                  Só marcados
                </label>
              </div>

              {itens.length === 0 ? (
                <p className="text-sm text-white/50">Nenhum item. Sincronize o cardápio ou ajuste a busca.</p>
              ) : (
                <ul className="divide-y divide-white/[0.06] rounded-2xl border border-white/[0.06] bg-white/[0.02]">
                  {itens.map(item => (
                    <li key={item.id}>
                      <button
                        type="button"
                        onClick={() => pdv.abrirEditar(item)}
                        className={`flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-white/[0.04] ${item.habilitado ? '' : 'opacity-50'}`}
                      >
                        {item.imagem_url ? (
                          <img src={item.imagem_url} alt="" className="h-10 w-10 rounded-xl object-cover" />
                        ) : (
                          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-orange-500/10 text-orange-300">
                            <UtensilsCrossed className="h-4 w-4" />
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium text-white">{item.nome_exibicao ?? item.nome}</p>
                          <p className="text-xs text-white/40">
                            {item.categoria ?? 'Sem categoria'} · R$ {item.preco?.toFixed(2) ?? '—'}
                          </p>
                        </div>
                        {!item.habilitado && (
                          <span className="rounded-full border border-white/10 px-2 py-0.5 text-[11px] font-semibold uppercase text-white/40">
                            inativo no PDV
                          </span>
                        )}
                        {item.papel !== 'nenhum' && (
                          <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold uppercase ${PAPEL_BADGE[item.papel]}`}>
                            {item.papel}
                          </span>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {/* Regras */}
            <section className={`${cardClass} space-y-3`}>
              <div>
                <h2 className="font-semibold text-white">Regras de exibição</h2>
                <p className="text-xs text-white/45">Defina as mesas monitoradas e os marcos que geram celebrações.</p>
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                <label className="text-xs text-white/60">
                  Mesas monitoradas (separadas por vírgula)
                  <input value={mesasTexto} onChange={e => setMesasTexto(e.target.value)} placeholder="1, 2, 3" className={inputClass} />
                </label>
                <label className="text-xs text-white/60">
                  Marcos de hype
                  <input value={marcosTexto} onChange={e => setMarcosTexto(e.target.value)} placeholder="25, 50, 100" className={inputClass} />
                </label>
                <label className="text-xs text-white/60">
                  Rótulo do hype ("50 chopps hoje!")
                  <input
                    value={regras.hype_rotulo}
                    onChange={e => setRegras(prev => ({ ...prev, hype_rotulo: e.target.value }))}
                    maxLength={30}
                    className={inputClass}
                  />
                </label>
                <label className="text-xs text-white/60">
                  Intervalo mínimo entre destaques (s)
                  <input
                    type="number"
                    min={30}
                    max={900}
                    value={regras.janela_destaque_seg}
                    onChange={e => setRegras(prev => ({ ...prev, janela_destaque_seg: Number(e.target.value) }))}
                    className={inputClass}
                  />
                </label>
              </div>
              <button
                type="button"
                onClick={salvarRegras}
                disabled={pdv.salvando}
                className="rounded-xl bg-gradient-to-r from-orange-500 to-rose-500 px-4 py-2.5 text-sm font-bold text-white disabled:opacity-40"
              >
                Salvar regras
              </button>
            </section>
          </div>
        )}

        {pdv.editando && (
          <PdvItemModal
            item={pdv.editando}
            salvando={pdv.salvando}
            onSalvar={m => { void pdv.salvarItem(m); }}
            onFechar={pdv.fecharModal}
            onUpload={pdv.enviarImagemItem}
          />
        )}
      </AppMain>
    </AppShell>
  );
}
